import { Controller } from "@hotwired/stimulus"

// Client-side search, rank filter, sorting, paging and column visibility for
// the group's keyword table. All rows are in the page already (a plan caps how
// many keywords a group can have), so this never re-requests anything.
export default class extends Controller {
  static targets = ["body", "search", "bucket", "summary", "prevButton", "nextButton"]
  static values = {
    pageSize: { type: Number, default: 25 },
    storageKey: String,
    sortStorageKey: { type: String, default: "sonar_keyword_sort" }
  }

  connect() {
    this.page = 0
    this.sortKey = null
    this.sortDir = 1
    this.rows = [...this.bodyTarget.querySelectorAll("tr")]
    this.restoreColumns()
    this.restoreSort()
    this.render()
  }

  filter() {
    this.page = 0
    this.render()
  }

  // Click, Enter or Space on a sortable header: toggles direction on the same
  // column, otherwise starts that column in its natural direction
  // (data-sort-first), then remembers the choice.
  sort(event) {
    const th = event.currentTarget
    const key = th.dataset.sortKey
    if (!key) return

    const first = th.dataset.sortFirst === "desc" ? -1 : 1
    this.applySort(key, this.sortKey === key ? -this.sortDir : first)
    this.saveSort()
    this.page = 0
    this.render()
  }

  applySort(key, dir) {
    this.sortKey = key
    this.sortDir = dir
    this.rows.sort((a, b) => {
      const av = this.valueFor(a, key)
      const bv = this.valueFor(b, key)
      const an = parseFloat(av), bn = parseFloat(bv)
      const cmp = Number.isNaN(an) || Number.isNaN(bn) ? av.localeCompare(bv) : an - bn
      // Ties keep a stable, readable order: alphabetical by keyword
      return cmp * dir || a.dataset.sortTerm.localeCompare(b.dataset.sortTerm)
    })
    this.rows.forEach((row) => this.bodyTarget.appendChild(row))
    this.updateIndicators()
  }

  // Date columns sort on their cell's own value (rank, 101 for not found, 9999 for no data)
  valueFor(row, key) {
    if (key.startsWith("date_")) {
      const cell = row.querySelector(`td[id$="_${key.slice(5)}"]`)
      return cell?.dataset.sortValue ?? "9999"
    }
    return row.dataset[`sort${this.cap(key)}`] ?? ""
  }

  updateIndicators() {
    this.element.querySelectorAll("th[data-sort-key]").forEach((th) => {
      const active = th.dataset.sortKey === this.sortKey
      const icon = th.querySelector(".sonar-sort-icon")
      th.setAttribute("aria-sort", active ? (this.sortDir === 1 ? "ascending" : "descending") : "none")
      if (icon) {
        icon.className = `bi sonar-sort-icon ${active ? (this.sortDir === 1 ? "bi-arrow-up-short" : "bi-arrow-down-short") : "bi-chevron-expand"}`
        icon.classList.toggle("is-active", active)
      }
    })
  }

  saveSort() {
    try { localStorage.setItem(this.sortStorageKeyValue, JSON.stringify({ key: this.sortKey, dir: this.sortDir })) } catch (_) { /* storage unavailable */ }
  }

  // Re-applies the last sort, as long as that column still exists here: a date
  // column from a different date range, or one hidden in the column picker,
  // is quietly ignored rather than sorting by something the seller can't see.
  restoreSort() {
    let saved
    try { saved = JSON.parse(localStorage.getItem(this.sortStorageKeyValue) || "null") } catch (_) { saved = null }
    if (!saved || (saved.dir !== 1 && saved.dir !== -1)) return

    const th = [...this.element.querySelectorAll("th[data-sort-key]")].find((el) => el.dataset.sortKey === saved.key)
    if (!th || th.hidden) return

    this.applySort(saved.key, saved.dir)
  }

  prev() { if (this.page > 0) { this.page -= 1; this.render() } }
  next() { if ((this.page + 1) * this.pageSizeValue < this.visible().length) { this.page += 1; this.render() } }

  toggleColumn(event) {
    const { column } = event.currentTarget.dataset
    this.applyColumn(column, event.currentTarget.checked)
    const hidden = this.hiddenColumns()
    if (event.currentTarget.checked) hidden.delete(column); else hidden.add(column)
    try { localStorage.setItem(this.storageKeyValue, JSON.stringify([...hidden])) } catch (_) { /* storage unavailable */ }
  }

  visible() {
    const q = this.hasSearchTarget ? this.searchTarget.value.trim().toLowerCase() : ""
    const bucket = this.hasBucketTarget ? this.bucketTarget.value : ""
    return this.rows.filter((row) => {
      if (q && !row.dataset.term.includes(q)) return false
      if (bucket === "__archived") return row.dataset.archived === "true"
      if (bucket && row.dataset.bucket !== bucket) return false
      return true
    })
  }

  render() {
    const matches = this.visible()
    const start = this.page * this.pageSizeValue
    const shown = new Set(matches.slice(start, start + this.pageSizeValue))
    this.rows.forEach((row) => { row.hidden = !shown.has(row) })

    const from = matches.length ? start + 1 : 0
    const to = Math.min(start + this.pageSizeValue, matches.length)
    this.summaryTarget.textContent = `${from} to ${to} of ${matches.length}`
    this.prevButtonTarget.disabled = this.page === 0
    this.nextButtonTarget.disabled = to >= matches.length
  }

  hiddenColumns() {
    try { return new Set(JSON.parse(localStorage.getItem(this.storageKeyValue) || "[]")) } catch (_) { return new Set() }
  }

  restoreColumns() {
    this.hiddenColumns().forEach((column) => {
      this.applyColumn(column, false)
      const box = this.element.querySelector(`input[data-column="${column}"]`)
      if (box) box.checked = false
    })
  }

  applyColumn(column, visible) {
    this.element.querySelectorAll(`[data-col="${column}"]`).forEach((el) => { el.hidden = !visible })
  }

  cap(key) { return key.charAt(0).toUpperCase() + key.slice(1) }
}
